import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import { CategoryOutlined } from '@mui/icons-material';

import { useAuthContext } from '../../context/auth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDialogStyles from '../../hooks/useDialogStyles';
import { useApplicationTypeContext } from '../../context/appType';
import { AddButton, DialogHeader, SubmitButton } from '../dialogKit';

const AddAppType = () => {
  const axiosPrivate = useAxiosPrivate();
  const { dispatchAppType } = useApplicationTypeContext();
  const { auth } = useAuthContext();
  const ds = useDialogStyles();

  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const nameRef = useRef('');

  const closeDialog = () => {
    if (saving) return;
    setShow(false);
    setError(null);
  };

  const handleAdd = async () => {
    if (!auth) {
      setError('You must be logged in');
      return;
    }

    const name = nameRef.current.value.trim();
    if (!name) {
      setError('Please enter a name');
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const response = await axiosPrivate.post('/api/appTypes', { name });

      dispatchAppType({ type: 'CREATE_APPLICATION_TYPE', payload: response.data });
      setError(null);
      setShow(false);
    } catch (error) {
      setError(error.response?.data.error || 'Unable to create the application type');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setError(null);
    if (nameRef.current) nameRef.current.value = '';
  };

  useEffect(() => {
    if (show) {
      resetForm();
    }
  }, [show]);

  return (
    <>
      <AddButton label="Add" onClick={() => setShow(true)} />

      <Dialog open={show} onClose={closeDialog} fullWidth maxWidth="xs" PaperProps={{ sx: ds.paperSx }}>
        <DialogHeader
          icon={CategoryOutlined}
          title="New application type"
          subtitle="Groups versions of the same application"
          onClose={closeDialog}
          disabled={saving}
        />

        <DialogContent sx={ds.contentSx}>
          <TextField
            fullWidth
            size="small"
            label="Name"
            placeholder="e.g. RadarClient"
            inputRef={nameRef}
            autoComplete="off"
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            sx={ds.fieldSx}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={ds.actionsSx}>
          <Button onClick={closeDialog} disabled={saving} sx={ds.cancelButtonSx}>
            Cancel
          </Button>
          <SubmitButton label="Add Type" busyLabel="Creating…" busy={saving} onClick={handleAdd} icon={<CategoryOutlined />} />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddAppType;
